import { CardShell, FieldShell } from '@atlas/ui-components'
import { Button, Combobox, type ComboboxOption, Input, Textarea } from '@atlas/ui-primitives'
import type { FormEvent } from 'react'
import { ClassifyPicker, optionLabel } from '@/components/classification/ClassifyPicker'
import { MESSAGE_STATUS_LABEL, MESSAGE_TYPE_LABEL } from '@/domain/message.types'
import type { RailAxis } from '@/hooks/useLibrary'
import { sortedByLabel } from '@/lib/option-order'
import { EnumField } from './field'
import type { MessageFormState } from './form-model'
import { OrderLinkField } from './OrderLinkField'

/** Language picker options, alphabetical — locale is a flat axis, so there is no hierarchy to preserve. */
function buildLocaleOptions(axes: RailAxis[]) {
	const localeAxis = axes.find((a) => a.code === 'locale')
	return sortedByLabel((localeAxis?.options ?? []).map((o) => ({ value: o.code, label: optionLabel(o.code, o.label) })))
}

/**
 * What the message is about, and where it is used: its classification tuple and its
 * work-order links. Two fields, not one, because they answer different questions —
 * the tuple describes the copy, `order_code` puts it ON an order — and only the
 * second makes it appear in a work order's Messages section.
 */
function DescriptorFields({
	form,
	set,
	axes,
	orderOptions,
}: {
	form: MessageFormState
	set: <K extends keyof MessageFormState>(key: K, value: MessageFormState[K]) => void
	axes: RailAxis[]
	orderOptions: ComboboxOption[]
}) {
	return (
		<>
			<FieldShell label="Classification" span={3}>
				<ClassifyPicker
					value={form.classification}
					axes={axes}
					onChange={(next) => set('classification', next)}
					emptyLabel="No axes attached — add one to classify this message."
				/>
			</FieldShell>

			<FieldShell label="Work orders" span={3}>
				<OrderLinkField value={form.orderCodes} options={orderOptions} onChange={(next) => set('orderCodes', next)} />
			</FieldShell>
		</>
	)
}

/**
 * Presentational message form body — prop-driven (no data-fetching), reused by
 * the New and Edit pages and covered by a Storybook story.
 */
export function MessageForm({
	form,
	set,
	axes = [],
	orderOptions = [],
	submitLabel,
	isPending,
	onSubmit,
	onCancel,
}: {
	form: MessageFormState
	set: <K extends keyof MessageFormState>(key: K, value: MessageFormState[K]) => void
	/** Registry axes for the language + classification pickers (from `useAxesWithValues`). */
	axes?: RailAxis[]
	/** Selectable work orders for the Work orders field (option value = order code). */
	orderOptions?: ComboboxOption[]
	submitLabel: string
	isPending: boolean
	onSubmit: () => void
	onCancel: () => void
}) {
	const localeOptions = buildLocaleOptions(axes)
	const submit = (e: FormEvent) => {
		e.preventDefault()
		onSubmit()
	}

	return (
		<form onSubmit={submit}>
			<CardShell as="section" className="p-6">
				<div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
					<FieldShell label="Title" htmlFor="title" span={3}>
						<Input
							id="title"
							value={form.title}
							onChange={(e) => set('title', e.target.value)}
							placeholder="e.g. Carousel card 1 — headline"
							required
						/>
					</FieldShell>

					<EnumField
						label="Type"
						value={form.messageType}
						labels={MESSAGE_TYPE_LABEL}
						onChange={(v) => set('messageType', v)}
					/>

					<EnumField
						label="Status"
						value={form.status}
						labels={MESSAGE_STATUS_LABEL}
						onChange={(v) => set('status', v)}
					/>

					<FieldShell label="Language">
						<Combobox
							aria-label="Language"
							options={localeOptions}
							value={form.languageIso || null}
							onChange={(v) => set('languageIso', v ?? '')}
							placeholder="Select language…"
						/>
					</FieldShell>

					<FieldShell label="Description" htmlFor="description" span={3}>
						<Input
							id="description"
							value={form.description}
							onChange={(e) => set('description', e.target.value)}
							placeholder="Internal note (optional)"
						/>
					</FieldShell>

					<FieldShell label="Content" htmlFor="content" span={3}>
						<Textarea
							id="content"
							value={form.content}
							onChange={(e) => set('content', e.target.value)}
							placeholder="The copy text"
							rows={4}
						/>
					</FieldShell>

					<DescriptorFields form={form} set={set} axes={axes} orderOptions={orderOptions} />
				</div>
			</CardShell>

			<div className="mt-6 flex items-center gap-3">
				<Button
					type="submit"
					isDisabled={!form.title.trim() || !form.content.trim() || isPending}
					className="rounded-ds-full px-6"
				>
					{isPending ? 'Saving…' : submitLabel}
				</Button>
				<Button type="button" variant="secondary" className="rounded-ds-full" onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</form>
	)
}
